// src/mapShowcase.js - СУПЕР-КЭШИРОВАНИЕ ДЛЯ ПЕРЕХОДОВ

class MapShowcase {
  constructor(map) {
    this.map = map;
    this.isPlaying = false;
    this.currentStep = 0;
    this.steps = [];
    this.isCaching = false;
    this.cacheProgress = 0;
    this.cachedSteps = new Set();
    this.recordingMode = false;
    
    // 📹 ВИДЕО ЗАПИСЬ - ОПТИМИЗИРОВАННАЯ
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.isRecordingVideo = false;
    this.videoQuality = 'web';
    this.loopedVideo = true; // 🔄 ПО УМОЛЧАНИЮ ЗАЦИКЛЕННЫЕ
  }

  // ⚙️ НАСТРОЙКИ КАЧЕСТВА ВИДЕО
  getVideoSettings(quality = 'web') {
    const settings = {
      // 📱 МОБИЛЬНОЕ - СОВСЕМ ЛЕГКОЕ (2-3 МБ)
      mobile: {
        fps: 30,
        bitrate: 1000000, // 1 Mbps
        scale: 0.5,
        duration: 2000,
        mimeType: 'video/webm;codecs=vp8'
      },
      
      // 🌐 ВЕБ - СРЕДНИЙ РАЗМЕР (5-8 МБ)
      web: {
        fps: 30,
        bitrate: 6000000, // 3 Mbps
        scale: 1.0,
        duration: 4000,
        mimeType: 'video/webm;codecs=vp8'
      },
      
      // 🎬 HD - ВЫСОКОЕ КАЧЕСТВО (15-20 МБ)
      hd: {
        fps: 30,
        bitrate: 6000000, // 6 Mbps
        scale: 1.0,
        duration: 4000,
        mimeType: 'video/webm;codecs=vp9'
      }
    };
    
    return settings[quality] || settings.web;
  }

  // 🎬 СОЗДАНИЕ ЗАЦИКЛЕННЫХ ШАГОВ ТУРА
  createTourSteps(quality = 'web') {
    const settings = this.getVideoSettings(quality);
    
    // 🔄 ОСНОВНЫЕ ШАГИ ТУРА
    const mainSteps = [
      {
        name: "Манхэттен",
        camera: { center: [-74.0060, 40.7128], zoom: 10, pitch: 0, bearing: 0 },
        duration: settings.duration,
        description: "Обзор Манхэттена",
        isMainStep: true
      },
      {
        name: "Уолл-Стрит",
        camera: { center: [-74.0113, 40.7074], zoom: 15, pitch: 60, bearing: 90 },
        duration: settings.duration,
        description: "Финансовый центр",
        isMainStep: true
      },
      {
        name: "Бруклинский мост",
        camera: { center: [-73.9969, 40.7061], zoom: 13, pitch: 35, bearing: 135 },
        duration: settings.duration,
        description: "Знаменитый мост",
        isMainStep: true
      },
      {
        name: "Empire State",
        camera: { center: [-73.9857, 40.7484], zoom: 15, pitch: 70, bearing: 180 },
        duration: settings.duration,
        description: "Легендарный небоскреб",
        isMainStep: true
      },
      {
        name: "Times Square",
        camera: { center: [-73.9857, 40.758], zoom: 14, pitch: 25, bearing: 225 },
        duration: settings.duration,
        description: "Сердце Нью-Йорка",
        isMainStep: true
      },
      {
        name: "Central Park",
        camera: { center: [-73.9654, 40.7829], zoom: 12, pitch: 15, bearing: 270 },
        duration: settings.duration,
        description: "Зеленые легкие",
        isMainStep: true
      }
    ];
    
    // Для мобильного - меньше точек
    if (quality === 'mobile') {
      this.steps = mainSteps.slice(0, 4); // Только 4 точки
    } else {
      this.steps = mainSteps;
    }
    
    // 🔄 ДОБАВЛЯЕМ ВОЗВРАТ К НАЧАЛУ ДЛЯ ЗАЦИКЛИВАНИЯ
    if (this.loopedVideo) {
      const firstStep = this.steps[0];
      const lastStep = this.steps[this.steps.length - 1];
      
      // 🌍 ШИРОКИЙ ПЕРЕХОДНЫЙ ШАГ (ТРЕБУЕТ МНОГО КЭШИРОВАНИЯ!)
      const transitionStep = {
        name: "Широкий переход",
        camera: {
          center: [
            (lastStep.camera.center[0] + firstStep.camera.center[0]) / 2,
            (lastStep.camera.center[1] + firstStep.camera.center[1]) / 2
          ],
          zoom: 9, // 🎯 ФИКСИРОВАННЫЙ ШИРОКИЙ ЗУМ
          pitch: 15, // Небольшой наклон
          bearing: 0 // Нулевой поворот
        },
        duration: settings.duration * 2.1,
        description: "Широкий обзор для перехода",
        isTransitionStep: true, // 🌟 МАРКЕР ДЛЯ СУПЕР-КЭШИРОВАНИЯ
        needsExtraCaching: true
      };
      
      // Возврат к началу (точная копия первого шага)
      const returnStep = {
        name: "Возврат к началу",
        camera: { ...firstStep.camera },
        duration: settings.duration,
        description: "Зацикливание",
        isReturnStep: true
      };
      
      this.steps.push(transitionStep);
      this.steps.push(returnStep);
      
      console.log(`🔄 Создан зацикленный тур: ${this.steps.length} шагов (включая переходы)`);
    }
  }

  // ✅ ВЫПОЛНЕНИЕ ШАГА ТУРА
  async executeStep(step) {
    console.log(`📍 ${step.name}: ${step.description}`);
    
    return new Promise((resolve) => {
      this.map.flyTo({
        ...step.camera,
        duration: step.duration,
        essential: true,
        curve: 1.42,
        speed: 0.8,
      });
      
      setTimeout(resolve, step.duration + 500);
    });
  }

  // 📹 ОПТИМИЗИРОВАННАЯ ЗАПИСЬ
  async startVideoRecording(quality = 'web') {
    try {
      console.log(`📹 Запись ${this.loopedVideo ? 'ЗАЦИКЛЕННОГО' : 'ОБЫЧНОГО'} видео: ${quality}`);
      
      const settings = this.getVideoSettings(quality);
      const mapCanvas = this.map.getCanvas();
      
      if (settings.scale < 1.0) {
        const originalWidth = mapCanvas.width;
        const originalHeight = mapCanvas.height;
        const newWidth = Math.floor(originalWidth * settings.scale);
        const newHeight = Math.floor(originalHeight * settings.scale);
        
        console.log(`📐 Размер: ${originalWidth}x${originalHeight} → ${newWidth}x${newHeight}`);
      }
      
      const stream = mapCanvas.captureStream(settings.fps);
      
      const options = {
        mimeType: settings.mimeType,
        videoBitsPerSecond: settings.bitrate
      };
      
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm;codecs=vp8';
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'video/webm';
        }
      }
      
      this.mediaRecorder = new MediaRecorder(stream, options);
      this.recordedChunks = [];
      this.currentQuality = quality;
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };
      
      this.mediaRecorder.onstop = () => {
        this.saveVideoFile();
      };
      
      this.mediaRecorder.start(1000);
      this.isRecordingVideo = true;
      
      console.log(`✅ 📹 Запись началась! Битрейт: ${settings.bitrate/1000000}Mbps, FPS: ${settings.fps}`);
      return true;
      
    } catch (error) {
      console.error('❌ Ошибка при начале записи:', error);
      return false;
    }
  }

  // ⏹️ ОСТАНОВИТЬ ЗАПИСЬ
  stopVideoRecording() {
    if (this.mediaRecorder && this.isRecordingVideo) {
      this.mediaRecorder.stop();
      this.isRecordingVideo = false;
      console.log('⏹️ Запись остановлена');
    }
  }

  // 💾 СОХРАНИТЬ ФАЙЛ
  saveVideoFile() {
    if (this.recordedChunks.length === 0) {
      console.warn('⚠️ Нет данных для сохранения');
      return;
    }

    const blob = new Blob(this.recordedChunks, {
      type: 'video/webm'
    });
    
    const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);
    console.log(`📊 Размер видео: ${sizeMB} МБ`);
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    
    const now = new Date();
    const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
    const loopSuffix = this.loopedVideo ? '-loop' : '';
    a.href = url;
    a.download = `nyc-tour-${this.currentQuality}${loopSuffix}-${timestamp}.webm`;
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log(`💾 ${this.loopedVideo ? 'Зацикленное' : 'Обычное'} видео сохранено: ${sizeMB} МБ`);
    this.showSaveNotification(`${this.loopedVideo ? '🔄 Зацикленное' : '📹 Обычное'} видео: ${sizeMB} МБ (${this.currentQuality})`);
  }

  // 💾 УВЕДОМЛЕНИЕ О СОХРАНЕНИИ
  showSaveNotification(message) {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 20000;
      background: #34C759;
      color: white;
      padding: 15px 25px;
      border-radius: 10px;
      font-weight: bold;
      box-shadow: 0 5px 15px rgba(0,0,0,0.3);
    `;
    notification.innerHTML = `💾 ${message}`;
    document.body.appendChild(notification);
    
    setTimeout(() => notification.remove(), 4000);
  }

  // 🙈 СКРЫТЬ UI
  hideUIElements() {
    const elementsToHide = [
      '#tour-controls',
      '.demo-search-container', 
      '[style*="position: absolute"][style*="bottom: 10px"][style*="right: 10px"]',
      '.maplibregl-ctrl-top-left',
      '.maplibregl-ctrl-top-right', 
      '.maplibregl-ctrl-bottom-left',
      '.maplibregl-ctrl-bottom-right',
      '.h3-controls',
      '#recording-indicator',
      '#cache-progress'
    ];

    this.hiddenElements = [];

    elementsToHide.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (el && el.style.display !== 'none') {
          this.hiddenElements.push({
            element: el,
            originalDisplay: el.style.display
          });
          el.style.display = 'none';
        }
      });
    });

    const allElements = document.querySelectorAll('*');
    allElements.forEach(el => {
      const style = window.getComputedStyle(el);
      const zIndex = parseInt(style.zIndex);
      
      if (zIndex > 500 && 
          el.id !== 'map' && 
          !el.closest('#map') &&
          el.style.display !== 'none') {
        this.hiddenElements.push({
          element: el,
          originalDisplay: el.style.display
        });
        el.style.display = 'none';
      }
    });

    console.log(`🙈 Скрыто ${this.hiddenElements.length} элементов`);
  }

  // 👁️ ПОКАЗАТЬ UI
  showUIElements() {
    if (this.hiddenElements) {
      this.hiddenElements.forEach(({element, originalDisplay}) => {
        element.style.display = originalDisplay;
      });
      console.log(`👁️ Восстановлено ${this.hiddenElements.length} элементов`);
      this.hiddenElements = [];
    }
  }

  // 🔄 ПЕРЕКЛЮЧИТЬ РЕЖИМ ЗАЦИКЛИВАНИЯ
  toggleLoopMode() {
    this.loopedVideo = !this.loopedVideo;
    console.log(`🔄 Режим зацикливания: ${this.loopedVideo ? 'ВКЛ' : 'ВЫКЛ'}`);
    return this.loopedVideo;
  }

  // 📱 МОБИЛЬНАЯ ЗАПИСЬ (2-3 МБ)
  async startMobileRecording() {
    if (this.isPlaying) return;
    
    this.hideUIElements();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const recordingStarted = await this.startVideoRecording('mobile');
    if (!recordingStarted) {
      this.showUIElements();
      return;
    }
    
    this.recordingMode = true;
    this.isPlaying = true;
    this.currentStep = 0;
    this.createTourSteps('mobile');
    
    console.log(`📱 МОБИЛЬНАЯ ЗАПИСЬ (2-3 МБ) - ${this.loopedVideo ? 'ЗАЦИКЛЕННАЯ' : 'ОБЫЧНАЯ'}`);
    
    for (let i = 0; i < this.steps.length; i++) {
      if (!this.isPlaying) break;
      this.currentStep = i;
      await this.executeStep(this.steps[i]);
    }
    
    this.stopVideoRecording();
    setTimeout(() => this.showUIElements(), 1000);
    this.isPlaying = false;
    this.recordingMode = false;
  }

  // 🌐 ВЕБ ЗАПИСЬ (5-8 МБ)
  async startWebRecording() {
    if (this.isPlaying) return;
    
    this.hideUIElements();
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const recordingStarted = await this.startVideoRecording('web');
    if (!recordingStarted) {
      this.showUIElements();
      return;
    }
    
    this.recordingMode = true;
    this.isPlaying = true;
    this.currentStep = 0;
    this.createTourSteps('web');
    
    console.log(`🌐 ВЕБ ЗАПИСЬ (5-8 МБ) - ${this.loopedVideo ? 'ЗАЦИКЛЕННАЯ' : 'ОБЫЧНАЯ'}`);
    
    for (let i = 0; i < this.steps.length; i++) {
      if (!this.isPlaying) break;
      this.currentStep = i;
      await this.executeStep(this.steps[i]);
    }
    
    this.stopVideoRecording();
    setTimeout(() => this.showUIElements(), 1000);
    this.isPlaying = false;
    this.recordingMode = false;
  }

  // 🗄️ СУПЕР-КЭШИРОВАНИЕ (ОСОБЕННО ДЛЯ ПЕРЕХОДОВ!)
  async cacheAllSteps() {
    if (this.isCaching) return;
    
    this.isCaching = true;
    this.cacheProgress = 0;
    this.cachedSteps.clear();
    this.createTourSteps('web'); // Создаем шаги с зацикливанием
    
    console.log(`🗄️ СУПЕР-КЭШИРОВАНИЕ ${this.loopedVideo ? 'ЗАЦИКЛЕННОГО' : 'ОБЫЧНОГО'} ТУРА...`);
    this.showCacheProgress('Начало супер-кэширования...', 0);
    
    for (let i = 0; i < this.steps.length; i++) {
      const step = this.steps[i];
      console.log(`📦 ${step.isTransitionStep ? 'СУПЕР-' : ''}кэширование ${i + 1}/${this.steps.length}: ${step.name}`);
      
      this.showCacheProgress(`${step.isTransitionStep ? 'СУПЕР-' : ''}кэширование: ${step.name}`, (i / this.steps.length) * 100);
      
      if (step.isTransitionStep || step.needsExtraCaching) {
        // 🌟 СУПЕР-КЭШИРОВАНИЕ ДЛЯ ПЕРЕХОДНЫХ ШАГОВ
        await this.superCacheStep(step, i);
      } else {
        // 📦 ОБЫЧНОЕ КЭШИРОВАНИЕ ДЛЯ ОБЫЧНЫХ ШАГОВ
        await this.deepCacheStep(step, i);
      }
      
      this.cachedSteps.add(i);
    }
    
    this.isCaching = false;
    this.showCacheProgress('✅ Супер-кэширование завершено!', 100);
    
    console.log('✅ ВСЕ ТАЙЛЫ СУПЕР-ЗАКЭШИРОВАНЫ!');
    
    setTimeout(() => {
      this.hideCacheProgress();
    }, 3000);
  }

  // 🌟💫 СУПЕР-КЭШИРОВАНИЕ ДЛЯ ПЕРЕХОДНЫХ ШАГОВ
  async superCacheStep(step, stepIndex) {
    console.log(`🌟 СУПЕР-КЭШИРОВАНИЕ переходного шага: ${step.name}`);
    
    return new Promise(async (resolve) => {
      // Быстро перелетаем к точке
      this.map.flyTo({
        ...step.camera,
        duration: 400,
        essential: true
      });
      
      // Ждем стабилизации
      await new Promise(r => setTimeout(r, 1000));
      
      // 💫 СУПЕР-МЕГА-КЭШИРОВАНИЕ ТАЙЛОВ ДЛЯ ШИРОКОГО ОБЗОРА
      await this.ultraLoadTilesAround(step.camera.center, step.camera.zoom);
      
      // Очень долгое время ожидания для полной загрузки
      setTimeout(resolve, 4000); // 4 секунды!
    });
  }

  // 📦🚀 ОБЫЧНОЕ ГЛУБОКОЕ КЭШИРОВАНИЕ
  async deepCacheStep(step, stepIndex) {
    return new Promise(async (resolve) => {
      this.map.flyTo({
        ...step.camera,
        duration: 600,
        essential: true
      });
      
      await new Promise(r => setTimeout(r, 800));
      await this.megaLoadTilesAround(step.camera.center, step.camera.zoom);
      setTimeout(resolve, 2000);
    });
  }

  // 💫🌍 УЛЬТРА-ЗАГРУЗКА ТАЙЛОВ (ДЛЯ ШИРОКИХ ОБЗОРОВ)
  async ultraLoadTilesAround(center, zoom) {
    console.log(`💫 УЛЬТРА-кэширование тайлов для широкого обзора [${center[0].toFixed(4)}, ${center[1].toFixed(4)}] зум ${zoom}`);
    
    // 🌍 ОГРОМНЫЙ РАДИУС ДЛЯ ШИРОКОГО ОБЗОРА
    const ultraRadius = 0.15; // ОГРОМНЫЙ радиус!
    const bigRadius = 0.08;
    const mediumRadius = 0.04;
    const smallRadius = 0.02;
    
    const allPoints = [];
    
    // 🎯 МЕГА-СЕТКА 15x15 = 225 точек
    for (let x = -7; x <= 7; x++) {
      for (let y = -7; y <= 7; y++) {
        allPoints.push([
          center[0] + (x * ultraRadius / 7),
          center[1] + (y * ultraRadius / 7)
        ]);
      }
    }
    
    // 🔄 МНОЖЕСТВЕННЫЕ КРУГИ
    const circles = [
      { radius: ultraRadius, points: 32 },
      { radius: bigRadius, points: 24 },
      { radius: mediumRadius, points: 16 },
      { radius: smallRadius, points: 12 }
    ];
    
    circles.forEach(circle => {
      for (let i = 0; i < circle.points; i++) {
        const angle = (i / circle.points) * Math.PI * 2;
        allPoints.push([
          center[0] + Math.cos(angle) * circle.radius,
          center[1] + Math.sin(angle) * circle.radius
        ]);
      }
    });
    
    // 📍 ДИАГОНАЛЬНЫЕ ЛИНИИ
    for (let i = 0; i < 20; i++) {
      const progress = i / 19;
      // Диагональ 1
      allPoints.push([
        center[0] + (-ultraRadius + progress * ultraRadius * 2),
        center[1] + (-ultraRadius + progress * ultraRadius * 2)
      ]);
      // Диагональ 2
      allPoints.push([
        center[0] + (ultraRadius - progress * ultraRadius * 2),
        center[1] + (-ultraRadius + progress * ultraRadius * 2)
      ]);
    }
    
    console.log(`💫 УЛЬТРА-кэширую ${allPoints.length} точек тайлов...`);
    
    // 🚀 ЗАГРУЖАЕМ ВСЕ ТОЧКИ С РАЗНЫМИ ЗУМАМИ
    for (let i = 0; i < allPoints.length; i++) {
      const point = allPoints[i];
      
      this.map.setCenter(point);
      this.map.setZoom(zoom);
      
      // 🎯 КЭШИРУЕМ НА РАЗНЫХ ЗУМАХ ДЛЯ НАДЕЖНОСТИ
      if (i % 8 === 0) {
        // Более широкий зум
        this.map.setZoom(Math.max(zoom - 2, 6));
        this.map.triggerRepaint();
        await new Promise(r => setTimeout(r, 100));
        
        // Средний зум
        this.map.setZoom(Math.max(zoom - 1, 7));
        this.map.triggerRepaint();
        await new Promise(r => setTimeout(r, 100));
        
        // Целевой зум
        this.map.setZoom(zoom);
        this.map.triggerRepaint();
        await new Promise(r => setTimeout(r, 100));
        
        // Более детальный зум
        this.map.setZoom(Math.min(zoom + 1, 16));
        this.map.triggerRepaint();
        await new Promise(r => setTimeout(r, 100));
      }
      
      this.map.triggerRepaint();
      
      // Более частые задержки для лучшей загрузки
      if (i % 3 === 0) {
        await new Promise(r => setTimeout(r, 150));
      }
    }
    
    // Возвращаемся в центр
    this.map.setCenter(center);
    this.map.setZoom(zoom);
    this.map.triggerRepaint();
    
    console.log(`✅ УЛЬТРА-кэширование завершено! Загружено ~${allPoints.length} точек`);
  }

  // 🌟 МЕГА-ЗАГРУЗКА ТАЙЛОВ ВОКРУГ (ОБЫЧНАЯ)
  async megaLoadTilesAround(center, zoom) {
    console.log(`🌟 Мега-кэширование тайлов вокруг [${center[0].toFixed(4)}, ${center[1].toFixed(4)}] зум ${zoom}`);
    
    const radius = 0.04;
    const smallRadius = 0.02;
    
    const gridPoints = [];
    
    // Сетка 7x7 = 49 точек
    for (let x = -3; x <= 3; x++) {
      for (let y = -3; y <= 3; y++) {
        gridPoints.push([
          center[0] + (x * radius / 3),
          center[1] + (y * radius / 3)
        ]);
      }
    }
    
    const circlePoints = [];
    for (let i = 0; i < 16; i++) {
      const angle = (i / 16) * Math.PI * 2;
      circlePoints.push([
        center[0] + Math.cos(angle) * radius,
        center[1] + Math.sin(angle) * radius
      ]);
      
      circlePoints.push([
        center[0] + Math.cos(angle) * smallRadius,
        center[1] + Math.sin(angle) * smallRadius
      ]);
    }
    
    const allPoints = [
      [center[0], center[1]],
      ...gridPoints,
      ...circlePoints
    ];
    
    console.log(`📦 Кэширую ${allPoints.length} точек тайлов...`);
    
    for (let i = 0; i < allPoints.length; i++) {
      const point = allPoints[i];
      
      this.map.setCenter(point);
      this.map.setZoom(zoom);
      
      if (i % 10 === 0) {
        this.map.setZoom(Math.max(zoom - 1, 8));
        this.map.triggerRepaint();
        await new Promise(r => setTimeout(r, 50));
        
        this.map.setZoom(Math.min(zoom + 1, 16));
        this.map.triggerRepaint();
        await new Promise(r => setTimeout(r, 50));
        
        this.map.setZoom(zoom);
      }
      
      this.map.triggerRepaint();
      
      if (i % 5 === 0) {
        await new Promise(r => setTimeout(r, 100));
      }
    }
    
    this.map.setCenter(center);
    this.map.setZoom(zoom);
    this.map.triggerRepaint();
    
    console.log(`✅ Мега-кэширование завершено для точки [${center[0].toFixed(4)}, ${center[1].toFixed(4)}]`);
  }

  // 📊 ПОКАЗАТЬ ПРОГРЕСС КЭШИРОВАНИЯ
  showCacheProgress(message, percent) {
    let progressDiv = document.getElementById('cache-progress');
    
    if (!progressDiv) {
      progressDiv = document.createElement('div');
      progressDiv.id = 'cache-progress';
      progressDiv.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 10000;
        background: rgba(0,0,0,0.95);
        color: white;
        padding: 30px;
        border-radius: 15px;
        text-align: center;
        min-width: 500px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.5);
        border: 2px solid #007AFF;
      `;
      document.body.appendChild(progressDiv);
    }
    
    progressDiv.innerHTML = `
      <h3 style="margin: 0 0 20px 0; color: #007AFF;">🌟 Супер-подготовка к записи</h3>
      <p style="margin: 0 0 15px 0; font-size: 14px;">${message}</p>
      <div style="background: #333; height: 12px; border-radius: 6px; overflow: hidden; margin: 0 0 10px 0; border: 1px solid #555;">
        <div style="background: linear-gradient(90deg, #007AFF, #00C7FF, #FF9F0A); height: 100%; width: ${percent}%; transition: width 0.3s ease; box-shadow: 0 0 15px rgba(0, 122, 255, 0.4);"></div>
      </div>
      <p style="margin: 0; font-size: 12px; opacity: 0.7;">${Math.round(percent)}% завершено</p>
      <p style="margin: 10px 0 0 0; font-size: 11px; opacity: 0.5;">Режим: ${this.loopedVideo ? 'Зацикленный' : 'Обычный'} | Кэширование: СУПЕР</p>
      <p style="margin: 5px 0 0 0; font-size: 10px; opacity: 0.4;">🌟 Основные точки: ~70 тайлов | 💫 Переходы: ~400 тайлов</p>
    `;
  }

  // 🙈 СКРЫТЬ ПРОГРЕСС
  hideCacheProgress() {
    const progressDiv = document.getElementById('cache-progress');
    if (progressDiv) {
      progressDiv.remove();
    }
  }

  // ⏹️ ОСТАНОВИТЬ ТУР
  stopTour() {
    this.isPlaying = false;
    this.recordingMode = false;
    
    if (this.isRecordingVideo) {
      this.stopVideoRecording();
      setTimeout(() => this.showUIElements(), 1000);
    }
    
    console.log('⏹️ Тур остановлен');
  }

  // 🗽 ОБЫЧНЫЙ ТУР (БЕЗ ЗАПИСИ)
  async startTour() {
    if (this.isPlaying) return;
    
    this.isPlaying = true;
    this.currentStep = 0;
    this.createTourSteps('web');
    
    console.log(`🎬 🗽 ${this.loopedVideo ? 'ЗАЦИКЛЕННЫЙ' : 'ОБЫЧНЫЙ'} ТУР ПО НЬЮ-ЙОРКУ!`);
    
    for (let i = 0; i < this.steps.length; i++) {
      if (!this.isPlaying) break;
      
      this.currentStep = i;
      await this.executeStep(this.steps[i]);
    }
    
    this.isPlaying = false;
    console.log('🎬 ✅ Тур завершен!');
  }
}

// 🎮 КОНТРОЛЛЕР С ЗАЦИКЛИВАНИЕМ
class TourController {
  constructor(map) {
    this.showcase = new MapShowcase(map);
    this.createControls();
    this.setupKeyboardControls();
  }

  setupKeyboardControls() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'F9') {
        event.preventDefault();
        this.showcase.startTour();
      }
      
      if (event.key === 'F10') {
        event.preventDefault();
        this.showcase.cacheAllSteps();
      }
      
      if (event.key === 'F11') {
        event.preventDefault();
        this.showcase.startWebRecording();
      }
      
      if (event.key === 'F12') {
        event.preventDefault();
        this.showcase.startMobileRecording();
      }
      
      if (event.key === 'L' && event.ctrlKey) {
        event.preventDefault();
        const looped = this.showcase.toggleLoopMode();
        console.log(`🔄 Зацикливание: ${looped ? 'ВКЛ' : 'ВЫКЛ'} (Ctrl+L)`);
        this.updateControlsUI();
      }
      
      if (event.key === 'Escape') {
        this.showcase.stopTour();
      }
    });
    
    console.log('⌨️ Горячие клавиши:');
    console.log('   F9 - Обычный тур');
    console.log('   F10 - СУПЕР-кэширование');  
    console.log('   F11 - Веб запись (5-8 МБ)');
    console.log('   F12 - Мобильная запись (2-3 МБ)');
    console.log('   Ctrl+L - Переключить зацикливание');
    console.log('   ESC - Остановка');
  }

  // 🎨 ОБНОВИТЬ UI КОНТРОЛОВ
  updateControlsUI() {
    const loopToggle = document.getElementById('loop-toggle');
    if (loopToggle) {
      const isLooped = this.showcase.loopedVideo;
      loopToggle.style.background = isLooped ? '#34C759' : '#8E8E93';
      loopToggle.innerHTML = isLooped ? '🔄 Loop ON' : '➡️ Loop OFF';
    }
  }

  createControls() {
    const existingControls = document.getElementById('tour-controls');
    if (existingControls) {
      existingControls.remove();
    }

    const controlsDiv = document.createElement('div');
    controlsDiv.id = 'tour-controls';
    controlsDiv.style.cssText = `
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000;
      background: rgba(0,0,0,0.9);
      padding: 15px;
      border-radius: 10px;
      display: flex;
      gap: 8px;
    `;
    
    const isLooped = this.showcase.loopedVideo;
    
    controlsDiv.innerHTML = `
      <button id="loop-toggle" style="background: ${isLooped ? '#34C759' : '#8E8E93'}; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        ${isLooped ? '🔄 Loop ON' : '➡️ Loop OFF'}
      </button>
      <button id="cache-tour" style="background: #FF9F0A; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        🌟 Супер-кэш (F10)
      </button>
      <button id="web-record" style="background: #007AFF; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        🌐 Веб 5МБ (F11)
      </button>
      <button id="mobile-record" style="background: #34C759; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        📱 Мини 2МБ (F12)
      </button>
      <button id="normal-tour" style="background: #8E8E93; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        🗽 Тур (F9)
      </button>
      <button id="stop-tour" style="background: #FF453A; color: white; border: none; padding: 8px 12px; border-radius: 5px; cursor: pointer; font-size: 11px;">
        ⏹️ Стоп
      </button>
    `;
    
    document.body.appendChild(controlsDiv);
    
    document.getElementById('loop-toggle').addEventListener('click', () => {
      this.showcase.toggleLoopMode();
      this.updateControlsUI();
    });
    
    document.getElementById('cache-tour').addEventListener('click', () => {
      this.showcase.cacheAllSteps();
    });
    
    document.getElementById('web-record').addEventListener('click', () => {
      this.showcase.startWebRecording();
    });
    
    document.getElementById('mobile-record').addEventListener('click', () => {
      this.showcase.startMobileRecording();
    });
    
    document.getElementById('normal-tour').addEventListener('click', () => {
      this.showcase.startTour();
    });
    
    document.getElementById('stop-tour').addEventListener('click', () => {
      this.showcase.stopTour();
    });
  }
}

export { MapShowcase, TourController };